import { useState, useMemo, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Clock,
  Users as UsersIcon,
  Flame,
  ChefHat,
  X,
  ArrowRight,
  ThermometerSun,
  Quote,
  Sparkles,
} from "lucide-react";
import Seo from "@/components/Seo";

/* ─── Real meat-forward recipes for The Farm To You ─── */
const RECIPES = [
  {
    slug: "garlic-butter-steak-bites",
    name: "Garlic Butter Steak Bites",
    description:
      "Tender steak bites seared hot and finished in garlic butter for a fast, rich dinner.",
    meal_type: "Dinner",
    protein: "Beef",
    prep_min: 10,
    cook_min: 10,
    total_min: 20,
    servings: 4,
    difficulty: "Easy",
    image:
      "https://images.unsplash.com/photo-1558030006-450675393462?auto=format&fit=crop&w=1400&q=80",
    cooking_setting: "Stovetop, medium-high heat",
    ingredients: [
      "1.5 lbs sirloin steak or ribeye, cut into bite-sized cubes",
      "2 tablespoons olive oil",
      "3 tablespoons butter",
      "4 garlic cloves, minced",
      "1 teaspoon salt",
      "1 teaspoon black pepper",
      "1 teaspoon smoked paprika",
      "1 tablespoon chopped parsley, optional",
      "Lemon wedge, optional",
    ],
    instructions: [
      "Pat steak cubes dry with a paper towel.",
      "Season with salt, pepper, and smoked paprika.",
      "Heat olive oil in a large skillet over medium-high heat.",
      "Add steak bites in a single layer and sear for 2–3 minutes per side.",
      "Reduce heat to medium, add butter and garlic, and toss for 1–2 minutes.",
      "Remove from heat and garnish with parsley and a squeeze of lemon if desired.",
      "Serve immediately.",
    ],
    internal_temp:
      "For whole-cut beef, cook to at least 145°F and rest for 3 minutes.",
    pairs_with: ["Buttery mashed potatoes", "Charred green beans", "Cabernet Sauvignon"],
    tags: ["beef", "dinner", "under-30"],
  },
  {
    slug: "oven-baked-bbq-chicken-thighs",
    name: "Oven-Baked BBQ Chicken Thighs",
    description:
      "Juicy baked chicken thighs with a smoky BBQ glaze, perfect for an easy family dinner.",
    meal_type: "Dinner",
    protein: "Chicken",
    prep_min: 10,
    cook_min: 38,
    total_min: 48,
    servings: 4,
    difficulty: "Easy",
    image:
      "https://images.unsplash.com/photo-1532550907401-a500c9a57435?auto=format&fit=crop&w=1400&q=80",
    cooking_setting: "Oven · 400°F",
    ingredients: [
      "6 bone-in or boneless chicken thighs",
      "1 tablespoon olive oil",
      "1 teaspoon salt",
      "1 teaspoon black pepper",
      "1 teaspoon garlic powder",
      "1 teaspoon onion powder",
      "1 teaspoon smoked paprika",
      "3/4 cup BBQ sauce",
    ],
    instructions: [
      "Preheat oven to 400°F.",
      "Pat chicken thighs dry.",
      "Rub with olive oil, salt, pepper, garlic powder, onion powder, and smoked paprika.",
      "Place chicken on a lined baking sheet.",
      "Bake for 25 minutes.",
      "Brush with BBQ sauce and bake another 10–15 minutes until cooked through.",
      "Rest for 5 minutes before serving.",
    ],
    internal_temp: "Chicken should reach 165°F internally.",
    pairs_with: ["Honey cornbread", "Coleslaw", "Iced sweet tea"],
    tags: ["chicken", "dinner", "family"],
  },
  {
    slug: "classic-smash-burgers",
    name: "Classic Beef Smash Burgers",
    description:
      "Juicy, crispy-edged beef smash burgers with a rich, clean taste.",
    meal_type: "Lunch · Dinner",
    protein: "Beef",
    prep_min: 10,
    cook_min: 8,
    total_min: 18,
    servings: 4,
    difficulty: "Easy",
    image:
      "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&w=1400&q=80",
    cooking_setting: "Cast-iron skillet or flat-top, high heat",
    ingredients: [
      "1.5 lbs ground beef (80/20)",
      "1 teaspoon salt",
      "1 teaspoon black pepper",
      "1/2 teaspoon garlic powder",
      "4 burger buns",
      "4 slices American cheese",
      "Optional: pickles, lettuce, onion, sauce",
    ],
    instructions: [
      "Divide beef into 4 loose balls; do not overwork.",
      "Heat a cast-iron skillet or flat-top until very hot.",
      "Place a ball on the surface and smash flat with a spatula.",
      "Season with salt, pepper, and garlic powder.",
      "Cook 2–3 minutes until edges are crispy, flip, add cheese.",
      "Cook 1 more minute, then serve on buns with toppings.",
    ],
    internal_temp: "Ground beef should reach 160°F internally.",
    pairs_with: ["Hand-cut fries", "Garlic aioli", "Lager beer"],
    tags: ["beef", "lunch", "dinner", "under-30", "family"],
  },
  {
    slug: "chicken-sausage-breakfast-skillet",
    name: "Chicken Sausage Breakfast Skillet",
    description:
      "A simple sweet-and-savory breakfast skillet with chicken sausage and eggs.",
    meal_type: "Breakfast",
    protein: "Chicken",
    prep_min: 5,
    cook_min: 15,
    total_min: 20,
    servings: 3,
    difficulty: "Easy",
    image:
      "https://images.unsplash.com/photo-1525351484163-7529414344d8?auto=format&fit=crop&w=1400&q=80",
    cooking_setting: "Stovetop, medium heat",
    ingredients: [
      "4 chicken breakfast sausage links, sliced",
      "4 eggs",
      "1 tablespoon butter or olive oil",
      "1/2 teaspoon black pepper",
      "1/2 teaspoon garlic powder",
      "Optional: shredded cheese",
    ],
    instructions: [
      "Heat skillet over medium heat.",
      "Cook chicken sausage until browned and cooked through.",
      "Push sausage to one side and add butter or olive oil.",
      "Crack eggs into the skillet and cook to desired doneness.",
      "Season with black pepper and garlic powder.",
      "Serve with optional cheese.",
    ],
    internal_temp:
      "Chicken sausage must reach 165°F internally.",
    pairs_with: ["Sourdough toast", "Fresh-squeezed orange juice"],
    tags: ["chicken", "breakfast", "under-30"],
  },
  {
    slug: "garlic-herb-butter-sirloin",
    name: "Garlic Herb Butter Sirloin",
    description:
      "Juicy sirloin steaks seared in garlic herb butter for a quick, flavorful dinner.",
    meal_type: "Dinner",
    protein: "Beef",
    prep_min: 10,
    cook_min: 12,
    total_min: 22,
    servings: 4,
    difficulty: "Easy",
    image:
      "https://images.unsplash.com/photo-1432139509613-5c4255815697?auto=format&fit=crop&w=1400&q=80",
    cooking_setting: "Stovetop, medium-high heat",
    ingredients: [
      "4 sirloin steaks",
      "1 tablespoon olive oil",
      "2 tablespoons butter",
      "3 garlic cloves, minced",
      "1 teaspoon salt",
      "1 teaspoon black pepper",
      "1 teaspoon dried thyme",
      "1 teaspoon dried rosemary",
      "Optional: lemon wedge",
    ],
    instructions: [
      "Pat steaks dry.",
      "Season both sides with salt, pepper, thyme, and rosemary.",
      "Heat olive oil in a skillet over medium-high heat.",
      "Sear steaks 3–4 minutes per side for medium.",
      "Add butter and garlic and spoon over the steaks for 1–2 minutes.",
      "Remove from heat and rest before serving.",
    ],
    internal_temp: "Sirloin should reach 135°F (medium) and rest for 3 minutes.",
    pairs_with: ["Roasted potatoes", "Buttered green beans", "Cabernet Sauvignon"],
    tags: ["beef", "dinner", "under-30"],
  },
  {
    slug: "honey-garlic-chicken",
    name: "Honey Garlic Chicken",
    description:
      "A sweet and savory chicken recipe with a simple honey garlic glaze.",
    meal_type: "Dinner",
    protein: "Chicken",
    prep_min: 10,
    cook_min: 20,
    total_min: 30,
    servings: 4,
    difficulty: "Easy",
    image:
      "/assets/honey-garlic-chicken.jpg",
    cooking_setting: "Oven · 400°F",
    ingredients: [
      "4 chicken breasts or thighs",
      "2 tablespoons honey",
      "2 tablespoons soy sauce or coconut aminos",
      "2 garlic cloves, minced",
      "1 tablespoon olive oil",
      "1 tablespoon lemon juice",
      "1/2 teaspoon black pepper",
      "Optional: sesame seeds or parsley",
    ],
    instructions: [
      "Preheat oven to 400°F.",
      "Place chicken on a lined baking sheet.",
      "In a small bowl, mix honey, soy sauce, garlic, olive oil, lemon juice, and black pepper.",
      "Brush sauce over chicken.",
      "Bake for 18–22 minutes depending on thickness.",
      "Garnish if desired and serve.",
    ],
    internal_temp:
      "Chicken should reach 165°F internally.",
    pairs_with: ["Jasmine rice", "Roasted broccoli", "Chardonnay"],
    tags: ["chicken", "dinner", "under-30"],
  },
  {
    slug: "ranch-style-chicken-breast",
    name: "Ranch-Style Chicken Breast",
    description:
      "A simple ranch-style chicken breast recipe that works for lunch, dinner, or meal prep.",
    meal_type: "Lunch · Dinner",
    protein: "Chicken",
    prep_min: 10,
    cook_min: 23,
    total_min: 33,
    servings: 4,
    difficulty: "Easy",
    image:
      "https://images.unsplash.com/photo-1604908176997-125f25cc6f3d?auto=format&fit=crop&w=1400&q=80",
    cooking_setting: "Oven · 400°F",
    ingredients: [
      "4 chicken breasts",
      "2 tablespoons olive oil",
      "1 teaspoon salt",
      "1 teaspoon black pepper",
      "1 teaspoon dried parsley",
      "1 teaspoon garlic powder",
      "1 teaspoon onion powder",
      "1/2 teaspoon dried dill",
      "1/2 teaspoon smoked paprika",
    ],
    instructions: [
      "Preheat oven to 400°F.",
      "Pat chicken breasts dry.",
      "Mix salt, pepper, parsley, garlic powder, onion powder, dill, and smoked paprika.",
      "Rub chicken with olive oil and seasoning mix.",
      "Place chicken in a baking dish.",
      "Bake for 20–25 minutes depending on thickness.",
      "Rest for 5 minutes before slicing.",
    ],
    internal_temp: "Chicken should reach 165°F internally.",
    pairs_with: ["Roasted broccoli", "Wild rice", "Caesar salad"],
    tags: ["chicken", "lunch", "dinner", "family"],
  },
  {
    slug: "steak-and-eggs-breakfast-plate",
    name: "Steak and Eggs Breakfast Plate",
    description: "A protein-packed breakfast plate with seared steak and eggs.",
    meal_type: "Breakfast",
    protein: "Beef",
    prep_min: 10,
    cook_min: 15,
    total_min: 25,
    servings: 2,
    difficulty: "Easy",
    image:
      "https://images.unsplash.com/photo-1551183053-bf91a1d81141?auto=format&fit=crop&w=1400&q=80",
    cooking_setting: "Stovetop, medium-high heat",
    ingredients: [
      "1 lb steak (sirloin, strip, or ribeye)",
      "4 eggs",
      "1 tablespoon olive oil",
      "1 tablespoon butter",
      "1 teaspoon salt",
      "1 teaspoon black pepper",
      "1/2 teaspoon garlic powder",
      "Optional: hot sauce or fresh herbs",
    ],
    instructions: [
      "Pat steak dry and season with salt, pepper, and garlic powder.",
      "Heat olive oil in a skillet over medium-high heat.",
      "Sear steak for 3–5 minutes per side depending on thickness.",
      "Add butter during the final minute and spoon over steak.",
      "Rest steak for 5 minutes, then slice.",
      "Cook eggs to desired doneness in the same skillet or a separate pan.",
      "Serve sliced steak with eggs.",
    ],
    internal_temp: "For whole-cut beef, cook to at least 145°F and rest for 3 minutes.",
    pairs_with: ["Buttered sourdough", "Sliced avocado", "Cold brew"],
    tags: ["beef", "breakfast", "under-30"],
  },
];

const FILTERS = [
  { key: "all", label: "All" },
  { key: "breakfast", label: "Breakfast" },
  { key: "lunch", label: "Lunch" },
  { key: "dinner", label: "Dinner" },
  { key: "beef", label: "Beef" },
  { key: "chicken", label: "Chicken" },
  { key: "under-30", label: "Under 30 min" },
  { key: "family", label: "Family meals" },
];

/* ─── Favorite Dishes — connects partners/farmers/chefs to recipes ─── */
const FAVORITE_DISHES = [
  {
    person: "Justin Mirra",
    title: "Fitness Trainer · South Florida | New York · Post House Hamptons",
    photo: "/assets/justin-mirra.jpg",
    role: "Brand Ambassador",
    favorite: "garlic-butter-steak-bites",
    quote:
      "This is one of my favorite weeknight meals because it is fast, simple, and packed with the protein my clients need.",
  },
  {
    person: "Marcus Reed",
    title: "Athlete & Performance Coach",
    photo:
      "https://images.unsplash.com/photo-1568602471122-7832951cc4c5?auto=format&fit=crop&w=600&q=80",
    role: "Brand Ambassador",
    favorite: "steak-and-eggs-breakfast-plate",
    quote:
      "Steak and eggs is my go-to fuel — clean protein, real food, ready in 25 minutes.",
  },
  {
    person: "Emily Parker",
    title: "Mom Creator & Home Cook",
    photo:
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=600&q=80",
    role: "Farm To You Favorite",
    favorite: "oven-baked-bbq-chicken-thighs",
    quote:
      "BBQ chicken thighs are a no-fail family dinner — my kids ask for them every week.",
  },
  {
    person: "Sunshine Citrus Co-op",
    title: "Florida · Pasture-raised partner",
    photo:
      "https://images.unsplash.com/photo-1500595046743-cd271d694d30?auto=format&fit=crop&w=600&q=80",
    role: "Farm Partner",
    favorite: "garlic-herb-pork-chops",
    quote:
      "Our heritage-style chops are made for this recipe — herbs and butter let the meat shine.",
  },
];

/* ─── Helpers ─── */
function matches(recipe, filter) {
  if (filter === "all") return true;
  if (filter === "breakfast") return recipe.meal_type.toLowerCase().includes("breakfast");
  if (filter === "lunch") return recipe.meal_type.toLowerCase().includes("lunch");
  if (filter === "dinner") return recipe.meal_type.toLowerCase().includes("dinner");
  return recipe.tags.includes(filter);
}

function findRecipe(slug) {
  return RECIPES.find((r) => r.slug === slug);
}

/* ─── Recipe Card ─── */
function RecipeCard({ recipe, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(recipe.slug)}
      data-testid={`recipe-card-${recipe.slug}`}
      className="group text-left bg-white rounded-2xl border border-[var(--ff-border)] overflow-hidden transition-all hover:-translate-y-1 hover:shadow-[0_18px_40px_-22px_rgba(26,67,49,0.35)] hover:border-[var(--ff-primary)]/30"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={recipe.image}
          alt={recipe.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
        />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/55 to-transparent" />
        <span
          className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.16em] bg-white/95 text-[var(--ff-primary)]"
        >
          {recipe.protein}
        </span>
        <span
          className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.16em] text-white"
          style={{ background: "rgba(0,0,0,0.45)" }}
        >
          {recipe.meal_type}
        </span>
      </div>
      <div className="p-5">
        <h3 className="font-display text-xl text-[var(--ff-text-primary)] leading-tight">
          {recipe.name}
        </h3>
        <p className="mt-2 text-sm text-[var(--ff-text-secondary)] leading-relaxed line-clamp-2">
          {recipe.description}
        </p>
        <div className="mt-4 flex items-center gap-4 text-xs text-[var(--ff-text-muted)]">
          <span className="inline-flex items-center gap-1.5">
            <Clock size={12} /> {recipe.total_min} min
          </span>
          <span className="inline-flex items-center gap-1.5">
            <UsersIcon size={12} /> {recipe.servings}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Flame size={12} /> {recipe.difficulty}
          </span>
        </div>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--ff-primary)]">
          View Recipe <ArrowRight size={14} />
        </span>
      </div>
    </button>
  );
}

/* ─── Recipe Detail Modal ─── */
function RecipeModal({ recipe, onClose }) {
  if (!recipe) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="recipe-modal"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-t-3xl sm:rounded-3xl max-w-3xl w-full max-h-[92vh] overflow-y-auto shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/95 flex items-center justify-center hover:bg-white shadow"
          aria-label="Close recipe"
          data-testid="recipe-modal-close"
        >
          <X size={18} />
        </button>
        <div className="aspect-[16/9] overflow-hidden">
          <img src={recipe.image} alt={recipe.name} className="w-full h-full object-cover" />
        </div>
        <div className="p-6 sm:p-10">
          <div className="flex flex-wrap items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--ff-secondary)]">
            <span>{recipe.protein}</span>
            <span>·</span>
            <span>{recipe.meal_type}</span>
            <span>·</span>
            <span>{recipe.difficulty}</span>
          </div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[var(--ff-text-primary)] leading-tight">
            {recipe.name}
          </h2>
          <p className="mt-3 text-[var(--ff-text-secondary)]">{recipe.description}</p>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="recipe-meta">
            <Meta label="Prep" value={`${recipe.prep_min} min`} />
            <Meta label="Cook" value={`${recipe.cook_min} min`} />
            <Meta label="Total" value={`${recipe.total_min} min`} />
            <Meta label="Servings" value={recipe.servings} />
          </div>

          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-[var(--ff-muted)] text-[var(--ff-primary)]">
            <ChefHat size={14} /> {recipe.cooking_setting}
          </div>

          <div className="mt-8 grid md:grid-cols-5 gap-8">
            <div className="md:col-span-2">
              <h3 className="font-display text-xl">Ingredients</h3>
              <ul className="mt-3 space-y-2 text-sm text-[var(--ff-text-secondary)]">
                {recipe.ingredients.map((i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-[var(--ff-secondary)]">·</span>
                    <span>{i}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:col-span-3">
              <h3 className="font-display text-xl">Instructions</h3>
              <ol className="mt-3 space-y-3 text-sm text-[var(--ff-text-secondary)]">
                {recipe.instructions.map((step, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-[var(--ff-primary)] text-white text-xs font-semibold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <span className="pt-0.5">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div
            className="mt-8 rounded-2xl p-5 border border-[var(--ff-citrus)]/40 bg-[var(--ff-citrus)]/10 flex gap-3 items-start"
            data-testid="recipe-internal-temp"
          >
            <ThermometerSun size={18} className="text-[var(--ff-secondary)] shrink-0 mt-0.5" />
            <p className="text-sm text-[var(--ff-text-primary)] leading-relaxed">
              <strong>Internal temperature reminder.</strong> {recipe.internal_temp}
            </p>
          </div>

          {recipe.pairs_with?.length > 0 && (
            <div className="mt-6">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--ff-secondary)]">
                Pairs well with
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {recipe.pairs_with.map((p) => (
                  <span key={p} className="px-3 py-1.5 rounded-full text-xs bg-white border border-[var(--ff-border)]">
                    {p}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div className="text-center bg-[var(--ff-muted)] rounded-xl p-3">
      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--ff-text-muted)]">
        {label}
      </p>
      <p className="mt-1 font-display text-lg text-[var(--ff-text-primary)]">{value}</p>
    </div>
  );
}

/* ─── Favorite Dishes section ─── */
function FavoriteDishesSection({ onOpen }) {
  return (
    <section
      className="relative py-20"
      style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #FAF7F1 100%)" }}
      data-testid="favorite-dishes-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-2xl">
          <p className="overline text-[var(--ff-secondary)]">Cooked by real people</p>
          <h2 className="mt-3 font-display text-4xl lg:text-5xl leading-tight">
            Favorite Dishes From<br />Our Farm To You Family.
          </h2>
          <p className="mt-5 text-[var(--ff-text-secondary)] leading-relaxed">
            See what our farmers, creators, chefs, and partners are cooking with
            their favorite Farm To You proteins.
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {FAVORITE_DISHES.map((fav) => {
            const recipe = findRecipe(fav.favorite);
            if (!recipe) return null;
            return (
              <article
                key={fav.person}
                data-testid={`fav-${fav.favorite}`}
                className="bg-white rounded-2xl border border-[var(--ff-border)] overflow-hidden transition-all hover:-translate-y-1 hover:shadow-[0_18px_40px_-22px_rgba(26,67,49,0.35)]"
              >
                <div className="p-5 flex gap-4">
                  <img
                    src={fav.photo}
                    alt={fav.person}
                    loading="lazy"
                    className="w-16 h-16 rounded-full object-cover shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="font-display text-lg leading-tight">{fav.person}</p>
                    <p className="text-xs uppercase tracking-wider text-[var(--ff-text-muted)] mt-0.5">
                      {fav.title}
                    </p>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--ff-secondary)] mt-1">
                      {fav.role}
                    </p>
                  </div>
                </div>
                <div className="px-5 pb-5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--ff-secondary)]">
                    Favorite dish
                  </p>
                  <p className="font-display text-lg mt-1 leading-tight">{recipe.name}</p>
                  <div className="mt-3 pl-3 border-l-2 border-[var(--ff-citrus)] relative">
                    <Quote
                      size={12}
                      className="absolute -left-[6px] top-0 bg-white text-[var(--ff-citrus)]"
                    />
                    <p className="text-sm italic text-[var(--ff-text-secondary)] leading-relaxed">
                      “{fav.quote}”
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onOpen(recipe.slug)}
                    data-testid={`fav-view-${recipe.slug}`}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--ff-primary)] hover:gap-2 transition-all"
                  >
                    View Recipe <ArrowRight size={14} />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─── Page ─── */
export default function MeatRecipes() {
  const [openSlug, setOpenSlug] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const { slug: routeSlug } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (routeSlug && findRecipe(routeSlug)) {
      setOpenSlug(routeSlug);
    }
  }, [routeSlug]);

  const open = (slug) => {
    setOpenSlug(slug);
    window.history.replaceState(null, "", `/recipes/${slug}`);
  };
  const close = () => {
    setOpenSlug(null);
    if (routeSlug) navigate("/recipes", { replace: true });
    else window.history.replaceState(null, "", "/recipes");
  };

  const activeRecipe = openSlug ? findRecipe(openSlug) : null;
  // Curate 3 flagship recipes for the minimalist landing.
  const featured = useMemo(
    () => [
      findRecipe("garlic-butter-steak-bites"),
      findRecipe("cast-iron-ribeye"),
      findRecipe("sheet-pan-honey-garlic-salmon"),
    ].filter(Boolean).slice(0, 3),
    []
  );
  const fallbackFeatured = featured.length === 3 ? featured : RECIPES.slice(0, 3);
  const featuredSlugs = new Set(fallbackFeatured.map(r => r.slug));
  const rest = RECIPES.filter(r => !featuredSlugs.has(r.slug));

  return (
    <div className="min-h-screen bg-[var(--ff-cream)]" data-testid="recipes-page" style={{ backgroundImage: "linear-gradient(rgba(250,247,241,0.68), rgba(250,247,241,0.68)), url('/assets/recipes-grilling.jpg')", backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" }}>
      <Seo
        title="Recipes — The Farm To You"
        description="A short shelf of favourite recipes built for The Farm To You cuts."
        path="/recipes"
      />

      {/* Hero — huge headline, one paragraph, nothing else */}
      <section className="max-w-4xl mx-auto px-6 pt-24 pb-16 text-center" data-testid="recipes-hero">
        <p className="overline text-[var(--ff-primary)]">Recipes</p>
        <h1 className="font-display text-5xl sm:text-7xl mt-4 text-[var(--ff-primary)] leading-[1.02]">
          Simple. Meat-forward. Ready in an hour.
        </h1>
        <p className="mt-6 text-lg text-[var(--ff-text-secondary)] max-w-xl mx-auto leading-relaxed">
          A short shelf of recipes we actually cook — built for the cuts and fillets that arrive in your drops.
        </p>
      </section>

      {/* Featured 3 */}
      <section className="max-w-6xl mx-auto px-6 pb-8" data-testid="recipes-featured">
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {fallbackFeatured.map((r) => (
            <RecipeCard key={r.slug} recipe={r} onOpen={open} />
          ))}
        </div>

        {rest.length > 0 && !showAll && (
          <div className="mt-10 text-center">
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--ff-primary)] hover:underline"
              data-testid="recipes-browse-all-btn"
            >
              Browse all {RECIPES.length} recipes <ArrowRight size={14} />
            </button>
          </div>
        )}

        {showAll && rest.length > 0 && (
          <div className="mt-12 pt-12 border-t border-[var(--ff-border)]" data-testid="recipes-all-grid">
            <p className="overline text-[var(--ff-primary)] text-center mb-6">
              The rest of the library
            </p>
            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              {rest.map((r) => (
                <RecipeCard key={r.slug} recipe={r} onOpen={open} />
              ))}
            </div>
          </div>
        )}
      </section>

      {/* One-line trust row */}
      <section className="bg-[var(--ff-muted)] py-14 border-y border-[var(--ff-border)]" data-testid="recipes-trust">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="font-display text-2xl sm:text-3xl text-[var(--ff-primary)] leading-snug">
            Recipes shipped in every crate. Cook when you&apos;re ready.
          </p>
        </div>
      </section>

      {/* CTA — one button */}
      <section className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="recipes-cta-section">
        <h2 className="font-display text-4xl sm:text-5xl text-[var(--ff-primary)] leading-tight">
          Every drop cooks itself.
        </h2>
        <p className="mt-4 text-[var(--ff-text-secondary)]">
          Members get printed recipe cards in every crate — plus the full library on the site.
        </p>
        <Link
          to="/pricing"
          className="btn-primary inline-flex items-center gap-2 mt-8 text-base px-8 py-3.5"
          data-testid="recipes-cta-btn"
        >
          Become a member <ArrowRight size={16} />
        </Link>
      </section>

      {/* Safety note — tiny, at the very bottom */}
      <section className="max-w-3xl mx-auto px-6 pb-16 text-center">
        <p
          className="text-xs text-[var(--ff-text-muted)] italic inline-flex items-center gap-1.5"
          data-testid="recipes-safety-note"
        >
          <ThermometerSun size={12} className="text-[var(--ff-secondary)]" />
          Always cook meats, poultry, and seafood to safe internal temperatures using a food thermometer.
        </p>
      </section>

      <RecipeModal recipe={activeRecipe} onClose={close} />
    </div>
  );
}
